import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { FadeUp } from "./Reveal";
import { Eyebrow } from "./shared";
import { REVIEWS, ZILLOW_PROFILE_URL, ZILLOW_REVIEW_URL, GOOGLE_PROFILE_URL } from "@/data/reviews";

const Testimonials = () => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % REVIEWS.length), 7500);
    return () => clearInterval(t);
  }, [paused]);

  const review = REVIEWS[idx];

  return (
    <div
      className="mx-auto max-w-4xl text-center"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="testimonials-slider"
    >
      <FadeUp>
        <a
          href={ZILLOW_PROFILE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="zillow-rating-badge"
          className="group mb-8 inline-flex items-center gap-3 border border-champagne/40 bg-gold-tint px-6 py-3 transition-colors duration-500 hover:border-champagne"
        >
          <span className="flex items-center gap-1" aria-hidden="true">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={13} className="fill-champagne text-champagne" />
            ))}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-charcoal">
            5.0 — 9 Verified Zillow Reviews
          </span>
        </a>
        <Eyebrow className="mb-4 justify-center">In Their Words</Eyebrow>
        <h2 className="font-serif text-4xl font-normal tracking-tight text-charcoal sm:text-5xl">Trusted by Our Clients</h2>
      </FadeUp>

      <FadeUp delay={0.15} className="relative mt-14">
        <Quote size={44} strokeWidth={0.75} className="mx-auto mb-8 text-champagne/50" aria-hidden="true" />
        <div className="min-h-[240px] sm:min-h-[200px]" aria-live="polite">
          <AnimatePresence mode="wait">
            <motion.figure
              key={idx}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.7, ease: [0.25, 1, 0.35, 1] }}
            >
              <blockquote className="font-serif text-2xl font-normal leading-relaxed text-charcoal sm:text-[1.75rem]" data-testid="testimonial-quote">
                “{review.text}”
              </blockquote>
              <figcaption className="mt-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-charcoal" data-testid="testimonial-author">
                  {review.name}
                </p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-muted">{review.context}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-muted/70">
                  {review.date} — Verified review via Zillow
                </p>
              </figcaption>
            </motion.figure>
          </AnimatePresence>
        </div>

        <div className="mt-10 flex items-center justify-center gap-6">
          <button
            onClick={() => setIdx((idx - 1 + REVIEWS.length) % REVIEWS.length)}
            aria-label="Previous review"
            data-testid="testimonial-prev"
            className="flex h-11 w-11 items-center justify-center border border-charcoal/20 text-charcoal transition-all duration-500 hover:border-champagne hover:bg-champagne hover:text-obsidian"
          >
            <ChevronLeft size={16} strokeWidth={1.5} />
          </button>
          <div className="flex items-center gap-2.5" role="tablist" aria-label="Reviews">
            {REVIEWS.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Show review ${i + 1}`}
                aria-selected={i === idx}
                role="tab"
                data-testid={`testimonial-dot-${i + 1}`}
                className={`h-px transition-all duration-700 ${i === idx ? "w-9 bg-champagne" : "w-4 bg-charcoal/25 hover:bg-charcoal/50"}`}
              />
            ))}
          </div>
          <button
            onClick={() => setIdx((idx + 1) % REVIEWS.length)}
            aria-label="Next review"
            data-testid="testimonial-next"
            className="flex h-11 w-11 items-center justify-center border border-charcoal/20 text-charcoal transition-all duration-500 hover:border-champagne hover:bg-champagne hover:text-obsidian"
          >
            <ChevronRight size={16} strokeWidth={1.5} />
          </button>
        </div>
      </FadeUp>

      <FadeUp delay={0.25} className="mt-16 border-t border-charcoal/10 pt-10">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-muted">Recently worked with Soby?</p>
        <p className="mt-3 font-serif text-2xl font-normal text-charcoal">Share your experience.</p>
        <div className="mt-6 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <a
            href={GOOGLE_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="review-google-btn"
            className="inline-flex items-center gap-3 border border-charcoal/30 px-8 py-4 font-mono text-[10px] uppercase tracking-[0.3em] text-charcoal transition-all duration-500 hover:border-champagne hover:text-champagne"
          >
            <Star size={13} strokeWidth={1.5} aria-hidden="true" /> Review on Google
          </a>
          <a
            href={ZILLOW_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="review-zillow-btn"
            className="inline-flex items-center gap-3 border border-charcoal/30 px-8 py-4 font-mono text-[10px] uppercase tracking-[0.3em] text-charcoal transition-all duration-500 hover:border-champagne hover:text-champagne"
          >
            <Star size={13} strokeWidth={1.5} aria-hidden="true" /> Review on Zillow
          </a>
        </div>
      </FadeUp>
    </div>
  );
};

export default Testimonials;
